import React, { useEffect, useRef, useState } from "react";
import {
    Save,
    Sparkles,
    MessageSquare,
    Plus,
    Trash2,
    Send,
    RotateCcw,
    Lightbulb,
    BookOpen,
    Hand,
    ShieldQuestion,
} from "lucide-react";
import PageHeader from "@/components/admin/PageHeader";
import { adminApi } from "@/lib/api";
import { toast } from "sonner";

const TEST_SESSION = "train-playground";

export default function TrainAgent() {
    const [settings, setSettings] = useState(null);
    const [savingCore, setSavingCore] = useState(false);

    const [snippet, setSnippet] = useState({ title: "", content: "" });
    const [savingSnippet, setSavingSnippet] = useState(false);

    const [qa, setQa] = useState([]);
    const [qaForm, setQaForm] = useState({ question: "", answer: "" });
    const [savingQa, setSavingQa] = useState(false);

    const [testInput, setTestInput] = useState("");
    const [testMsgs, setTestMsgs] = useState([]);
    const [testing, setTesting] = useState(false);
    const testEndRef = useRef(null);

    const loadSettings = () =>
        adminApi.get("/api/admin/settings/ai").then((r) => setSettings(r.data));
    const loadQa = () =>
        adminApi.get("/api/admin/qa").then((r) => setQa(r.data.items || []));

    useEffect(() => {
        loadSettings();
        loadQa();
    }, []);

    useEffect(() => {
        testEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [testMsgs]);

    if (!settings) return <div className="p-10 text-stone-500">Loading…</div>;

    const saveCore = async () => {
        setSavingCore(true);
        try {
            await adminApi.put("/api/admin/settings/ai", {
                welcome_message: settings.welcome_message,
                base_instructions: settings.base_instructions,
                fallback_message: settings.fallback_message,
                escalation: settings.escalation || {},
            });
            toast.success(`Agent updated — ${settings.name || "the agent"} will use this immediately`);
        } catch (e) {
            toast.error("Couldn't save changes — please try again");
        } finally {
            setSavingCore(false);
        }
    };

    const setEsc = (key, val) =>
        setSettings((s) => ({ ...s, escalation: { ...(s.escalation || {}), [key]: val } }));

    const addSnippet = async () => {
        if (!snippet.title.trim() || !snippet.content.trim()) {
            toast.error("Add a title and the fact to teach");
            return;
        }
        setSavingSnippet(true);
        try {
            await adminApi.post("/api/admin/snippets", snippet);
            setSnippet({ title: "", content: "" });
            toast.success("Fact saved to this agent's knowledge");
            loadSettings();
        } catch (e) {
            toast.error("Couldn't save the fact — please try again");
        } finally {
            setSavingSnippet(false);
        }
    };

    const addQa = async () => {
        if (!qaForm.question.trim() || !qaForm.answer.trim()) {
            toast.error("Both question and answer are required");
            return;
        }
        setSavingQa(true);
        try {
            await adminApi.post("/api/admin/qa", qaForm);
            setQaForm({ question: "", answer: "" });
            toast.success("Q&A added");
            loadQa();
            loadSettings();
        } catch (e) {
            toast.error("Couldn't add the Q&A — please try again");
        } finally {
            setSavingQa(false);
        }
    };

    const deleteQa = async (id) => {
        try {
            await adminApi.delete(`/api/admin/qa/${id}`);
            loadQa();
            loadSettings();
        } catch (e) {
            toast.error("Couldn't delete that Q&A — please try again");
        }
    };

    const runTest = async () => {
        const q = testInput.trim();
        if (!q || testing) return;
        setTestInput("");
        setTestMsgs((m) => [...m, { role: "user", text: q }]);
        setTesting(true);
        try {
            const r = await adminApi.post("/api/admin/playground", {
                question: q,
                session_id: TEST_SESSION,
            });
            setTestMsgs((m) => [...m, { role: "bot", text: r.data.answer }]);
        } catch (e) {
            setTestMsgs((m) => [
                ...m,
                { role: "bot", text: "Something went wrong testing that. Please try again." },
            ]);
        } finally {
            setTesting(false);
        }
    };

    const resetTest = async () => {
        await adminApi.post(`/api/admin/playground/reset?session_id=${TEST_SESSION}`);
        setTestMsgs([]);
    };

    const kb = settings.kb || {};
    const agentName = settings.name || "Priya";
    const isHr = settings.kind === "hr";
    const esc = settings.escalation || {};

    return (
        <div className="p-10 max-w-6xl" data-testid="page-train-agent">
            <PageHeader
                eyebrow="Agent"
                title={`Train ${agentName}`}
                testid="page-train-agent-header"
            />
            <p className="text-stone-600 -mt-4 mb-6 max-w-3xl">
                Teach <strong>{agentName}</strong> everything in one place — the greeting, personality,
                facts, and exact Q&amp;A. Everything you save here applies to <strong>{agentName}</strong> only
                and is used immediately. {agentName} answers strictly from {agentName}&apos;s own resources.
            </p>

            {/* Knowledge status */}
            <div
                className="mb-8 border border-sage-500/30 bg-sage-500/5 rounded-lg p-4 flex items-center gap-3"
                data-testid="train-kb-status"
            >
                <Sparkles className="text-sage-700 shrink-0" size={18} />
                <div className="text-sm text-stone-700">
                    {agentName} is currently grounded on{" "}
                    <strong>{kb.documents ?? 0}</strong> documents,{" "}
                    <strong>{kb.qa_pairs ?? 0}</strong> Q&amp;A pairs,{" "}
                    <strong>{kb.snippets ?? 0}</strong> taught facts and{" "}
                    <strong>{kb.website_chunks ?? 0}</strong> website pages. New files &amp;
                    links are learned automatically.
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* LEFT column: teach */}
                <div className="space-y-6">
                    {/* Greeting */}
                    <section className="bg-white border border-stone-200 rounded-lg p-6">
                        <h2 className="font-heading text-xl text-orchid-700 mb-1 flex items-center gap-2">
                            <Hand size={18} /> Greeting
                        </h2>
                        <p className="text-xs text-stone-500 mb-4">
                            The first message guests see when they open the chat.
                        </p>
                        <textarea
                            rows={3}
                            value={settings.welcome_message || ""}
                            onChange={(e) =>
                                setSettings({ ...settings, welcome_message: e.target.value })
                            }
                            data-testid="train-welcome"
                            className="w-full px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-orchid-700 focus:ring-2 focus:ring-orchid-700/15 leading-relaxed"
                        />
                    </section>

                    {/* Persona / instructions */}
                    <section className="bg-white border border-stone-200 rounded-lg p-6">
                        <h2 className="font-heading text-xl text-orchid-700 mb-1 flex items-center gap-2">
                            <BookOpen size={18} /> Personality &amp; rules
                        </h2>
                        <p className="text-xs text-stone-500 mb-4">
                            Tell {agentName} who they are and how to behave. This is the core
                            instruction set.
                        </p>
                        <textarea
                            rows={12}
                            value={settings.base_instructions || ""}
                            onChange={(e) =>
                                setSettings({ ...settings, base_instructions: e.target.value })
                            }
                            data-testid="train-instructions"
                            className="w-full px-3 py-2 rounded-md border border-stone-200 text-sm font-mono focus:outline-none focus:border-orchid-700 focus:ring-2 focus:ring-orchid-700/15 leading-relaxed"
                        />
                    </section>

                    {/* Fallback */}
                    <section className="bg-white border border-stone-200 rounded-lg p-6">
                        <h2 className="font-heading text-xl text-orchid-700 mb-1 flex items-center gap-2">
                            <ShieldQuestion size={18} /> When she doesn&apos;t know
                        </h2>
                        <p className="text-xs text-stone-500 mb-4">
                            Used only for off-topic questions or things truly not in your
                            resources. Include a phone/email so guests have a next step.
                        </p>
                        <textarea
                            rows={3}
                            value={settings.fallback_message || ""}
                            onChange={(e) =>
                                setSettings({ ...settings, fallback_message: e.target.value })
                            }
                            data-testid="train-fallback"
                            className="w-full px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-orchid-700 focus:ring-2 focus:ring-orchid-700/15 leading-relaxed"
                        />
                    </section>

                    {/* Escalation contacts (HR / employee agents) */}
                    {isHr && (
                        <section className="bg-white border border-teal-500/30 rounded-lg p-6" data-testid="train-escalation">
                            <h2 className="font-heading text-xl text-teal-700 mb-1 flex items-center gap-2">
                                <Hand size={18} /> HR / IT escalation contacts
                            </h2>
                            <p className="text-xs text-stone-500 mb-4">
                                Shown to employees whenever {agentName} can&apos;t answer, and when they tap
                                &quot;Talk to HR&quot;. Saved with the settings below.
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <label className="text-[11px] uppercase tracking-wider text-stone-500 block mb-1">HR Email</label>
                                    <input
                                        value={esc.hr_email || ""}
                                        onChange={(e) => setEsc("hr_email", e.target.value)}
                                        placeholder="hr@orchidhotel.com"
                                        data-testid="train-esc-hr-email"
                                        className="w-full px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-600/15"
                                    />
                                </div>
                                <div>
                                    <label className="text-[11px] uppercase tracking-wider text-stone-500 block mb-1">HR Phone</label>
                                    <input
                                        value={esc.hr_phone || ""}
                                        onChange={(e) => setEsc("hr_phone", e.target.value)}
                                        placeholder="+91 …"
                                        data-testid="train-esc-hr-phone"
                                        className="w-full px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-600/15"
                                    />
                                </div>
                                <div>
                                    <label className="text-[11px] uppercase tracking-wider text-stone-500 block mb-1">IT Support Email</label>
                                    <input
                                        value={esc.it_email || ""}
                                        onChange={(e) => setEsc("it_email", e.target.value)}
                                        placeholder="itsupport@orchidhotel.com"
                                        data-testid="train-esc-it-email"
                                        className="w-full px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-600/15"
                                    />
                                </div>
                                <div>
                                    <label className="text-[11px] uppercase tracking-wider text-stone-500 block mb-1">IT Support Phone</label>
                                    <input
                                        value={esc.it_phone || ""}
                                        onChange={(e) => setEsc("it_phone", e.target.value)}
                                        placeholder="+91 …"
                                        data-testid="train-esc-it-phone"
                                        className="w-full px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-600/15"
                                    />
                                </div>
                                <div className="sm:col-span-2">
                                    <label className="text-[11px] uppercase tracking-wider text-stone-500 block mb-1">Note (optional)</label>
                                    <input
                                        value={esc.note || ""}
                                        onChange={(e) => setEsc("note", e.target.value)}
                                        placeholder="e.g. HR desk open Mon–Sat, 10am–6pm"
                                        data-testid="train-esc-note"
                                        className="w-full px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-600/15"
                                    />
                                </div>
                            </div>
                        </section>
                    )}

                    <button
                        onClick={saveCore}
                        disabled={savingCore}
                        data-testid="train-save-core"
                        className="inline-flex items-center gap-2 bg-orchid-700 hover:bg-orchid-600 text-white text-sm font-medium px-5 py-2.5 rounded-md disabled:opacity-60"
                    >
                        <Save size={14} /> {savingCore ? "Saving…" : `Save greeting, personality${isHr ? ", fallback & contacts" : " & fallback"}`}
                    </button>

                    {/* Teach a fact */}
                    <section className="bg-white border border-stone-200 rounded-lg p-6">
                        <h2 className="font-heading text-xl text-orchid-700 mb-1 flex items-center gap-2">
                            <Lightbulb size={18} /> Teach a fact
                        </h2>
                        <p className="text-xs text-stone-500 mb-4">
                            Quickly add knowledge in plain language (e.g. a policy, a phone
                            number, a special offer). Saved as a knowledge snippet.
                        </p>
                        <input
                            value={snippet.title}
                            onChange={(e) => setSnippet({ ...snippet, title: e.target.value })}
                            placeholder="Topic (e.g. Pet policy)"
                            data-testid="train-snippet-title"
                            className="w-full mb-2 px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-orchid-700 focus:ring-2 focus:ring-orchid-700/15"
                        />
                        <textarea
                            rows={3}
                            value={snippet.content}
                            onChange={(e) =>
                                setSnippet({ ...snippet, content: e.target.value })
                            }
                            placeholder="The fact in plain words (e.g. Small pets up to 10 kg allowed, Rs 1500/night cleaning fee)"
                            data-testid="train-snippet-content"
                            className="w-full mb-3 px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-orchid-700 focus:ring-2 focus:ring-orchid-700/15 leading-relaxed"
                        />
                        <button
                            onClick={addSnippet}
                            disabled={savingSnippet}
                            data-testid="train-snippet-add"
                            className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-md border border-orchid-700/30 text-orchid-700 hover:bg-orchid-700/5 disabled:opacity-60"
                        >
                            <Plus size={14} /> {savingSnippet ? "Teaching…" : `Teach ${agentName}`}
                        </button>
                    </section>

                    {/* Q&A */}
                    <section className="bg-white border border-stone-200 rounded-lg p-6">
                        <h2 className="font-heading text-xl text-orchid-700 mb-1 flex items-center gap-2">
                            <MessageSquare size={18} /> Exact Q&amp;A
                        </h2>
                        <p className="text-xs text-stone-500 mb-4">
                            For questions that need an exact, controlled answer.
                        </p>
                        <input
                            value={qaForm.question}
                            onChange={(e) => setQaForm({ ...qaForm, question: e.target.value })}
                            placeholder="Question (e.g. What is check-in time?)"
                            data-testid="train-qa-question"
                            className="w-full mb-2 px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-orchid-700 focus:ring-2 focus:ring-orchid-700/15"
                        />
                        <textarea
                            rows={2}
                            value={qaForm.answer}
                            onChange={(e) => setQaForm({ ...qaForm, answer: e.target.value })}
                            placeholder="Exact answer"
                            data-testid="train-qa-answer"
                            className="w-full mb-3 px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-orchid-700 focus:ring-2 focus:ring-orchid-700/15 leading-relaxed"
                        />
                        <button
                            onClick={addQa}
                            disabled={savingQa}
                            data-testid="train-qa-add"
                            className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-md border border-orchid-700/30 text-orchid-700 hover:bg-orchid-700/5 disabled:opacity-60"
                        >
                            <Plus size={14} /> {savingQa ? "Adding…" : "Add Q&A"}
                        </button>

                        {qa.length > 0 && (
                            <ul className="mt-5 space-y-2" data-testid="train-qa-list">
                                {qa.map((item) => (
                                    <li
                                        key={item.id}
                                        className="flex items-start gap-3 border-t border-stone-100 pt-2 text-sm"
                                    >
                                        <div className="flex-1 min-w-0">
                                            <div className="font-medium text-stone-900">
                                                {item.question}
                                            </div>
                                            <div className="text-stone-600 mt-0.5">{item.answer}</div>
                                        </div>
                                        <button
                                            onClick={() => deleteQa(item.id)}
                                            data-testid={`train-qa-delete-${item.id}`}
                                            className="text-stone-300 hover:text-red-700 shrink-0"
                                        >
                                            <Trash2 size={15} />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>
                </div>

                {/* RIGHT column: live test */}
                <div className="lg:sticky lg:top-6 self-start">
                    <section className="bg-white border border-stone-200 rounded-lg overflow-hidden flex flex-col h-[640px]">
                        <div className="px-5 py-4 border-b border-stone-100 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Sparkles size={16} className="text-orchid-700" />
                                <span className="font-heading text-lg text-orchid-700">
                                    Try {agentName}
                                </span>
                            </div>
                            <button
                                onClick={resetTest}
                                data-testid="train-test-reset"
                                className="inline-flex items-center gap-1 text-xs text-stone-500 hover:text-orchid-700"
                            >
                                <RotateCcw size={13} /> Reset
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-5 space-y-3 bg-stone-50/50">
                            {testMsgs.length === 0 && (
                                <div className="text-sm text-stone-400 text-center mt-10">
                                    Ask anything to test what {agentName} has learned.
                                </div>
                            )}
                            {testMsgs.map((m, i) => (
                                <div
                                    key={i}
                                    className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                                >
                                    <div
                                        data-testid={`train-test-msg-${m.role}`}
                                        className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${
                                            m.role === "user"
                                                ? "bg-orchid-700 text-white rounded-br-sm"
                                                : "bg-white border border-stone-200 text-stone-800 rounded-bl-sm"
                                        }`}
                                    >
                                        {m.text}
                                    </div>
                                </div>
                            ))}
                            {testing && (
                                <div className="flex justify-start">
                                    <div className="bg-white border border-stone-200 text-stone-400 rounded-2xl rounded-bl-sm px-4 py-2.5 text-sm">
                                        {agentName} is typing…
                                    </div>
                                </div>
                            )}
                            <div ref={testEndRef} />
                        </div>

                        <div className="p-4 border-t border-stone-100 flex items-center gap-2">
                            <input
                                value={testInput}
                                onChange={(e) => setTestInput(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && runTest()}
                                placeholder="Type a guest question…"
                                data-testid="train-test-input"
                                className="flex-1 px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-orchid-700 focus:ring-2 focus:ring-orchid-700/15"
                            />
                            <button
                                onClick={runTest}
                                disabled={testing}
                                data-testid="train-test-send"
                                className="inline-flex items-center justify-center bg-orchid-700 hover:bg-orchid-600 text-white rounded-md w-10 h-10 disabled:opacity-60"
                            >
                                <Send size={16} />
                            </button>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
}
